import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { useCanUseProVoices } from '@/hooks/useCanUseProVoices';
import CloneVoice from '@/images/CloneVoice.svg';
import FaceFemale from '@/images/FaceFemale.svg';
import FaceMale from '@/images/FaceMale.svg';
import FlashLightning2 from '@/images/FlashLightning2.svg';
import Stars from '@/images/Stars.svg';
import Trash from '@/images/Trash.svg';
import theme from '@/theme';
import { getLanguageFlagSrc } from '@/utils/getLangaugeFlagSrc';
import { isProPlus } from '@/utils/isProPlus';
import { IVoice, Providers } from '@d-id/types';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@mui/material';
import { Instance } from '@popperjs/core';
import mixpanel from 'mixpanel-browser';
import { observer } from 'mobx-react-lite';
import { MutableRefObject, useRef, useState } from 'react';
import { isClonedVoice } from '../../../../../hooks/useClonedVoices';
import { getVoiceInfo } from '../../../voiceInfo';
import { Tag } from '../../../VoiceTag';
import { styles, UpgradeNeededTooltipContent } from '../../UpgradeNeededTooltipContent';
import { FavouriteButton } from './FavouriteButton';
import { RowVoicePlayer } from './RowVoicePlayer';

const StyledVoiceRow = styled.li<{ active?: boolean; disabled?: boolean }>`
    height: 75px;
    border-radius: 10px;
    border: 1.5px solid ${theme.colors.black[300]};
    cursor: pointer;
    display: flex;
    padding: 11px 27px 13px 20px;
    align-items: center;

    :hover {
        background-color: ${theme.colors.black[50]};
        border: 1.5px solid ${theme.colors.secondary[900]};
    }

    ${({ active }) =>
        active &&
        `
        background-color: ${theme.colors.primary.bgLight} !important;
        border: 1.5px solid ${theme.colors.primary[900]} !important;
    `}

    ${({ disabled }) =>
        disabled &&
        `
        cursor: not-allowed;
    `}
`;

const StyledDivider = styled.div`
    margin: 0 16px;
    background-color: #dddee0;
    border-right-width: 2px;
    border-radius: 2px;
    height: 23px;
    min-width: 2px;
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
`;

const VoiceContainer = styled(Typography)`
    display: flex;
    color: ${theme.colors.secondary[900]};
    gap: 4px;
    font-size: 16px;
    font-weight: 500;
    align-items: center;

    svg {
        margin-right: 8px;
    }
`;

const FlashLightningIcon = styled(MaskedIcon)`
    color: ${theme.colors.primary[900]};
    margin: 0;
`;

const LanguageContainer = styled(Typography)`
    display: flex;
    color: ${theme.colors.secondary[700]};
    gap: 4px;
    font-size: 14px;
    text-transform: capitalize;
    align-items: center;

    svg {
        margin-right: 8px;
    }
`;

const Actions = styled.div`
    display: flex;
    flex-direction: row;
`;

const LanguageImage = styled.img`
    width: 16px;
    margin: 0 2px;
`;

const DeprecationTooltip = styled.div`
    border-radius: 4px;
    color: white;
    font-family: Manrope;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-align: left;
    max-width: 250px;
`;

const ExpressiveIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background-color: #d4e7ff;
    opacity: 0.85;
    border-radius: 4px;
    backdrop-filter: blur(10px);

    svg {
        margin: 0;
    }
`;

const genderImage: Record<string, string> = {
    female: FaceFemale,
    male: FaceMale,
    'non-binary': CloneVoice,
};

const getGenderIcon = (voice: IVoice): JSX.Element | null => {
    const gender = voice.gender;

    return gender && gender !== 'unspecified' ? (
        <img loading="lazy" width="20" height="20" src={genderImage[gender.toLowerCase()]} alt="start adornment" />
    ) : null;
};

const getLanguageIcon = (value: IVoice): JSX.Element | null => {
    const src = getLanguageFlagSrc(value);
    return src ? <LanguageImage loading="lazy" src={src} alt={value.language} /> : null;
};

interface Props {
    voice: IVoice;
    onDelete: () => void;
    onSelect: () => void;
    isSelected: boolean;
    pauseCallbackRef: MutableRefObject<VoidFunction | null>;
    personal?: boolean;
    showLowLatencyTag?: boolean;
    isExpressiveVoice?: boolean;
}

export const VoiceRow = observer(
    ({ voice, onDelete, onSelect, isSelected, pauseCallbackRef, personal, showLowLatencyTag, isExpressiveVoice }: Props) => {
        const canUseProVoices = useCanUseProVoices();

        const [hovered, setHovered] = useState(false);

        const positionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
        const popperRef = useRef<Instance>(null);
        const areaRef = useRef<HTMLLIElement>(null);

        const isProVoice = isProPlus(voice);
        const handleMouseMove = (event: React.MouseEvent<HTMLLIElement>) => {
            positionRef.current = { x: event.clientX, y: event.clientY };

            if (popperRef.current != null) {
                popperRef.current.update();
            }
        };

        const getClonedVoiceInfo = () => {
            if (isClonedVoice(voice)) {
                return (
                    <>
                        <LanguageImage src={CloneVoice} />
                        Cloned voice
                    </>
                );
            }
            return null;
        };
        const showTrashIcon = hovered && isClonedVoice(voice) && voice.voiceType != 'Avatar';
        const isDisabled = !canUseProVoices && isProVoice;

        return (
            <>
                <Tooltip
                    title={
                        <UpgradeNeededTooltipContent
                            text="Premium voices are available on our Pro plan"
                            onUpgradeClick={() => {
                                mixpanel?.track('upgrade-package', { event: 'click', from: 'premium-voice' });
                            }}
                        />
                    }
                    placement="top"
                    disableHoverListener={!isDisabled}
                    slotProps={styles}
                    PopperProps={{
                        popperRef,
                        anchorEl: {
                            getBoundingClientRect: () => {
                                return new DOMRect(positionRef.current.x, areaRef.current!.getBoundingClientRect().y, 0, 0);
                            },
                        },
                    }}>
                    <StyledVoiceRow
                        data-testid={isSelected ? 'selected-voice-row' : `voice-row${personal ? '-personal' : ''}`}
                        className="voice_panel__voice_item"
                        ref={areaRef}
                        onMouseMove={handleMouseMove}
                        onClick={isDisabled ? undefined : onSelect}
                        active={isSelected}
                        disabled={isDisabled}
                        onMouseEnter={e => {
                            handleMouseMove(e);
                            setHovered(true);
                        }}
                        onMouseLeave={() => setHovered(false)}>
                        <div>
                            <RowVoicePlayer voice={voice} pauseCallbackRef={pauseCallbackRef} />
                        </div>
                        <StyledDivider />
                        <InfoContainer>
                            <VoiceContainer>
                                {getGenderIcon(voice)}
                                <span className="voice-row__text">{voice.name}</span>
                                {isProVoice && <FlashLightningIcon src={FlashLightning2} size="16px" />}
                                {showLowLatencyTag && voice.provider === Providers.Microsoft && <Tag green>Low latency</Tag>}
                                {voice.deprecationMessage && (
                                    <CustomTooltip
                                        placement="top"
                                        title={<DeprecationTooltip>{voice.deprecationMessage}</DeprecationTooltip>}
                                        sx={{ marginBottom: '5px' }}>
                                        <Tag>Deprecating </Tag>
                                    </CustomTooltip>
                                )}
                                {isExpressiveVoice && (
                                    <CustomTooltip
                                        placement="top"
                                        title="This voice delivers speech with realistic intonation "
                                        slotProps={{
                                            tooltip: {
                                                sx: {
                                                    maxWidth: '272px !important',
                                                    padding: '16px',
                                                },
                                            },
                                        }}>
                                        <ExpressiveIconWrapper>
                                            <MaskedIcon src={Stars} size="14px" color={theme.colors.links.main} />
                                        </ExpressiveIconWrapper>
                                    </CustomTooltip>
                                )}
                            </VoiceContainer>
                            <LanguageContainer>
                                {getLanguageIcon(voice)}
                                {getVoiceInfo(voice)}
                                {getClonedVoiceInfo()}
                            </LanguageContainer>
                        </InfoContainer>
                        <Actions>
                            <IconButton
                                onClick={event => {
                                    event.stopPropagation();
                                    onDelete();
                                }}
                                ariaLabel="delete-voice"
                                show={showTrashIcon}>
                                <MaskedIcon src={Trash} size="24px" color={theme.colors.secondary.main} />
                            </IconButton>
                            <FavouriteButton voice={voice} />
                        </Actions>
                    </StyledVoiceRow>
                </Tooltip>
            </>
        );
    }
);
